import { computed, ref } from 'vue'

import dayjs from 'dayjs'
import { defineStore } from 'pinia'

import { createEventSchema, initialFormData } from '../components/CreateEventFrom/schema'

import type { EventFormDataType, MonthlyRepeatOption } from '../types/events'
import type * as yup from 'yup'

/**
 * Create Events UI Store
 * Owns the create/edit event form state and exposes only direct value setters
 * plus derived (computed) state. All form business rules and orchestration
 * (field rules, day toggling, recurring params, edit conversion, submit, step
 * navigation) live in the `useCreateEvent` composable.
 */
export const useCreateEventsUIStore = defineStore('create-events-ui', () => {
  // Local state
  const formData = ref<EventFormDataType>(initialFormData())
  const touchedFields = ref<Record<string, boolean>>({})
  const currentStep = ref(1)
  const isFormTouched = ref(false)
  const isCreatingEvent = ref(false)

  // Validation is pure derived state: re-run the (synchronous) schema whenever
  // the form changes. Errors are surfaced only for touched fields (or once the
  // whole form has been touched via a submit attempt). Computing this — rather
  // than mutating state from a deep watcher — keeps the store side-effect free
  // and avoids duplicate validation when multiple components consume the
  // `useCreateEvent` composable at once.
  // Keep this in the store (not composable) so all mounted consumers share one
  // cached validation source instead of each instance re-running schema checks.
  const validationResult = computed<{ isValid: boolean; errors: Record<string, string> }>(() => {
    try {
      createEventSchema().validateSync(formData.value, { abortEarly: false })
      return { isValid: true, errors: {} }
    } catch (error) {
      const errors: Record<string, string> = {}
      ;(error as yup.ValidationError).inner.forEach(err => {
        if (err.path && (touchedFields.value[err.path] || isFormTouched.value)) {
          errors[err.path] = err.message
        }
      })
      return { isValid: false, errors }
    }
  })

  const isFormValid = computed(() => validationResult.value.isValid)
  const validationErrors = computed(() => validationResult.value.errors)

  // Monthly recurrence label options derived from the chosen start time.
  const monthlyRepeatOptions = computed<MonthlyRepeatOption[]>(() => {
    const newDate = formData.value.startTime
    if (!newDate) return []

    const dayOfMonth = dayjs(newDate).format('D')
    const dayOfWeekKey = dayjs(newDate).format('dddd').toLowerCase()
    const weekOfMonth = Math.ceil(dayjs(newDate).date() / 7)
    const ordinalKeys = ['first', 'second', 'third', 'fourth', 'fifth']
    const ordinalKey = ordinalKeys[weekOfMonth - 1]

    const options: MonthlyRepeatOption[] = [
      {
        labelKey: 'events.recurring.monthlyOnDay',
        labelParams: { day: dayOfMonth },
        value: 'monthly_day',
      },
      {
        labelKey: 'events.recurring.monthlyOnThe',
        labelParams: {
          ordinal: `events.recurring.ordinals.${ordinalKey}`,
          day: `events.options.dayOfWeek.${dayOfWeekKey}`,
        },
        value: 'monthly_weekday',
      },
    ]
    return options
  })

  // ===== Direct value setters =====
  const setFormData = (data: EventFormDataType) => {
    formData.value = data
  }

  // Direct write of a (possibly nested) field. Creates intermediate objects for
  // paths that don't exist yet. No business rules — callers in the composable
  // own those.
  const setFormField = (key: string, value: unknown) => {
    const keys = key.split('.')
    let current: any = formData.value
    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]] === undefined) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = value
  }

  const setTouchedField = (key: string) => {
    touchedFields.value[key] = true
  }

  const resetTouched = () => {
    touchedFields.value = {}
  }

  // Direct reset of all form state to defaults.
  const resetForm = () => {
    formData.value = initialFormData()
    touchedFields.value = {}
    isFormTouched.value = false
    currentStep.value = 1
  }

  const setCurrentStep = (step: number) => {
    currentStep.value = step
  }

  const setFormTouched = (value: boolean) => {
    isFormTouched.value = value
  }

  const setIsCreatingEvent = (value: boolean) => {
    isCreatingEvent.value = value
  }

  return {
    // State (computed for reactivity, not readonly)
    formData: computed(() => formData.value),
    currentStep: computed(() => currentStep.value),
    isCreatingEvent: computed(() => isCreatingEvent.value),
    // Derived state
    isFormValid,
    validationErrors,
    monthlyRepeatOptions,
    // Direct value setters
    setFormData,
    setFormField,
    setTouchedField,
    resetTouched,
    resetForm,
    setCurrentStep,
    setFormTouched,
    setIsCreatingEvent,
  }
})
